import React, { useEffect, useRef } from "react";

// Effet "Silk" (façon reactbits.dev), réécrit en interne en Canvas 2D plutôt qu'en WebGL/OGL —
// pas de nouvelle dépendance, coût CPU négligeable (quelques dégradés radiaux par frame, jamais
// de traitement pixel par pixel). Volontairement 100% monochrome : aucune teinte, seulement du
// blanc à faible opacité sur le fond sombre du parent — trois nappes de lumière qui dérivent
// lentement + un reflet diagonal qui balaie doucement, pour un effet de tissu qui capte la
// lumière plutôt qu'un fond plat.
//
// Le parent DOIT être position:"relative" ou position:"absolute" — ce canvas se cale en
// position:absolute; inset:0 dessus et se redimensionne avec lui (ResizeObserver).
export default function Silk({ className, style }: { className?: string; style?: React.CSSProperties }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let raf = 0;
    let w = 0, h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = parent.getBoundingClientRect();
      w = Math.max(1, Math.round(rect.width));
      h = Math.max(1, Math.round(rect.height));
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(parent);

    const blobs = [
      { baseX: 0.3, baseY: 0.35, speed: 0.00011, phase: 0, alpha: 0.10 },
      { baseX: 0.7, baseY: 0.6, speed: 0.00014, phase: 2.1, alpha: 0.08 },
      { baseX: 0.5, baseY: 0.85, speed: 0.00009, phase: 4.2, alpha: 0.07 },
    ];

    const draw = (t: number) => {
      ctx.clearRect(0, 0, w, h);

      for (const b of blobs) {
        const cx = (b.baseX + Math.sin(t * b.speed + b.phase) * 0.12) * w;
        const cy = (b.baseY + Math.cos(t * b.speed * 0.8 + b.phase) * 0.12) * h;
        const r = Math.max(w, h) * 0.55;
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        grad.addColorStop(0, `rgba(255,255,255,${b.alpha})`);
        grad.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      }

      // Reflet diagonal qui balaie lentement, façon soie qui capte la lumière en bougeant.
      const sweep = ((t * 0.00006) % 1.6) - 0.3;
      const sheen = ctx.createLinearGradient(w * (sweep - 0.25), 0, w * (sweep + 0.25), h);
      sheen.addColorStop(0, "rgba(255,255,255,0)");
      sheen.addColorStop(0.5, "rgba(255,255,255,0.05)");
      sheen.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = sheen;
      ctx.fillRect(0, 0, w, h);

      if (!reduceMotion) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={className}
      style={{ position: "absolute", inset: 0, display: "block", ...style }}
    />
  );
}
